import { useId } from "react";

import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import { CompoundAiVerificationStage } from "@/app/features/diagrams/components/blog/decomposedCompoundAi/CompoundAiVerificationStage";
import {
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

// Dev-mode topology specification check for referential integrity
const pipelineTopologySpec: DiagramTopologySpec = {
  nodes: [
    "raw-input",
    "retrieval-engine",
    "intent-classifier",
    "bounded-llm",
    "compliance-guardrails",
    "verified-output",
  ],
  junctions: [
    { id: "split-input", kind: "split", x: 350, y: 110 },
    { id: "merge-context", kind: "merge", x: 350, y: 198 },
  ],
  connectors: [
    {
      id: "input-to-split",
      source: { nodeId: "raw-input", anchor: "bottom-center" },
      target: { junctionId: "split-input" },
      meaning: "Raw input enters parallel processing split",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "split-to-retrieval",
      source: { junctionId: "split-input" },
      target: { nodeId: "retrieval-engine", anchor: "top-center" },
      meaning: "Factual context retrieval branch",
      semanticType: "keyword",
      lineStyle: "solid",
    },
    {
      id: "split-to-classifier",
      source: { junctionId: "split-input" },
      target: { nodeId: "intent-classifier", anchor: "top-center" },
      meaning: "Intent classification branch",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "retrieval-to-merge",
      source: { nodeId: "retrieval-engine", anchor: "bottom-center" },
      target: { junctionId: "merge-context" },
      meaning: "Retrieved facts flow to merge junction",
      semanticType: "keyword",
      lineStyle: "solid",
    },
    {
      id: "classifier-to-merge",
      source: { nodeId: "intent-classifier", anchor: "bottom-center" },
      target: { junctionId: "merge-context" },
      meaning: "Classified intent flows to merge junction",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "merge-to-llm",
      source: { junctionId: "merge-context" },
      target: { nodeId: "bounded-llm", anchor: "top-center" },
      meaning: "Merged context enters bounded LLM",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "llm-to-guardrails",
      source: { nodeId: "bounded-llm", anchor: "bottom-center" },
      target: { nodeId: "compliance-guardrails", anchor: "top-center" },
      meaning: "Draft payload enters deterministic guardrails",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "guardrails-to-output",
      source: { nodeId: "compliance-guardrails", anchor: "bottom-center" },
      target: { nodeId: "verified-output", anchor: "top-center" },
      meaning: "Validated payload flows to verified output",
      semanticType: "success",
      lineStyle: "solid",
    },
  ],
};

if (process.env.NODE_ENV !== "production") {
  validateDiagramTopology(pipelineTopologySpec);
}

export function DecomposedCompoundAiArchitectureVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={660}
      height={450}
      title="Decomposed Compound AI System Architecture"
      description="Pipeline diagram showing raw input split into deterministic factual retrieval and targeted intent classification, merged into a context-bounded LLM draft generator, validated by rule-based compliance guardrails into verified output."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 700 470"
      frame={{
        kind: "boundary",
        label: "COMPOUND SYSTEM BOUNDARY",
        x: 20,
        y: 20,
        width: 660,
        height: 430,
      }}
    >
      <DiagramMarkers prefix="ca-" />

      {/* STAGE 1: Raw Input (Y: 50 to 94) */}
      <DiagramNode
        x={200}
        y={50}
        width={300}
        height={44}
        label="Raw Operator Input"
        sublabel="User Intent & Unstructured Request"
        variant="capsule"
      />

      {/* Stem from Input to Split Junction (350, 110) */}
      <path
        d="M 350 94 L 350 110"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* Split Junction Connectors */}
      <path
        d="M 350 110 L 190 110 L 190 126"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#ca-arrow-neutral)"
      />

      <path
        d="M 350 110 L 510 110 L 510 126"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#ca-arrow-neutral)"
      />

      {/* Split Junction Dot */}
      <circle cx="350" cy="110" r="3.5" fill="var(--ga-brass, #9c6b3e)" />

      {/* STAGE 2: Parallel Engines (Deterministic Retrieval & Intent Classifier, Y: 128 to 180) */}
      <DiagramNode
        x={70}
        y={128}
        width={240}
        height={52}
        label="Deterministic Retrieval"
        sublabel="Search Index & Knowledge Base"
        variant="standard"
      />

      <DiagramNode
        x={390}
        y={128}
        width={240}
        height={52}
        label="Intent Classifier"
        sublabel="Targeted 3B / Fine-Tuned Model"
        variant="standard"
      />

      {/* Merge Connectors to Merge Junction (350, 198) */}
      <path
        d="M 190 180 L 190 198 L 350 198"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      <path
        d="M 510 180 L 510 198 L 350 198"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* Merge Junction Dot */}
      <circle cx="350" cy="198" r="3.5" fill="var(--ga-brass, #9c6b3e)" />

      {/* Stem down to Bounded LLM */}
      <path
        d="M 350 198 L 350 216"
        fill="none"
        stroke="#3b82f6"
        strokeWidth="1.6"
        strokeDasharray="5 4"
        markerEnd="url(#ca-arrow-blue)"
      />

      {/* STAGE 3: Context-Bounded LLM (Y: 218 to 266) */}
      <DiagramNode
        x={190}
        y={218}
        width={320}
        height={48}
        label="Context-Bounded LLM"
        sublabel="Draft Generation Restricted to Context"
        variant="llm"
      />

      {/* Stem 3 -> Compliance Guardrails */}
      <path
        d="M 350 266 L 350 296"
        fill="none"
        stroke="var(--ga-brass, #9c6b3e)"
        strokeWidth="1.6"
        markerEnd="url(#ca-arrow-neutral)"
      />

      <CompoundAiVerificationStage />
    </DiagramFrame>
  );
}
